import { api } from '../api/client';
import {
	API_EXPORT_DEFS_URL,
	API_EXPORT_DEF_URL,
	API_EXPORT_RUNS_URL,
	API_EXPORT_RUN_FILE_URL,
} from '../utils';

/**
 * STORED EXPORTS — an export as a noun (keepr-api docs/SCHEMA.md § Stored
 * exports).
 *
 * services/exports.js is the VERB: fetch a document now, for whoever holds
 * the token. This module is the NOUN: a named definition that says what to
 * export, how, on what cadence and to whom, with a ledger of its runs.
 *
 * Every route is manage-gated by the API; every WRITE also needs a real
 * session (an API key gets a 403) and a verified account. Nothing here
 * re-checks that — the tab that calls it is manager-only and the server has
 * the last word.
 *
 * ⚠ THE SERVER OWNS EVERY DEFAULT, and it is strict: an unknown key anywhere
 * in a payload is 400 `unknown_key`, a wrong type 400 `wrong_type`. The API's
 * messages are written for a person and name the field, so callers surface
 * `error.response.data.message` verbatim.
 *
 * ⚠ OWNERSHIP MOVES. A run renders through its definition's `ownerId`, and
 * every PATCH and every enable re-owns the definition to the caller. The
 * editor and the list say so before either happens.
 */

/**
 * The listing: `{ exports, channels, limits }`. `channels` says which
 * delivery channels can actually deliver (`rcs` is `{ available: false,
 * reason: 'channel_not_built' }`), and `limits` states the caps the editor
 * shows — both ride the listing so the editor needs no second request.
 */
export async function getExportDefinitions( collectionId ) {
	const { data } = await api.get( API_EXPORT_DEFS_URL( collectionId ) );
	return {
		exports: Array.isArray( data?.exports ) ? data.exports : [],
		channels: data?.channels || {},
		limits: data?.limits || {},
	};
}

export async function getExportDefinition( collectionId, exportId ) {
	const { data } = await api.get( API_EXPORT_DEF_URL( collectionId, exportId ) );
	return data;
}

/** 201 Definition; the caller becomes its owner. */
export async function createExportDefinition( collectionId, payload ) {
	const { data } = await api.post( API_EXPORT_DEFS_URL( collectionId ), payload );
	return data;
}

/** Partial update — and it re-owns the definition to the caller. */
export async function patchExportDefinition( collectionId, exportId, payload ) {
	const { data } = await api.patch( API_EXPORT_DEF_URL( collectionId, exportId ), payload );
	return data;
}

/** Soft delete: `{ ok, revokedDownloads }` — every link it handed out stops. */
export async function deleteExportDefinition( collectionId, exportId ) {
	const { data } = await api.delete( API_EXPORT_DEF_URL( collectionId, exportId ) );
	return data;
}

/** Enable re-owns the definition to the caller and clears `disabledReason`. */
export async function enableExportDefinition( collectionId, exportId ) {
	const { data } = await api.post( `${ API_EXPORT_DEF_URL( collectionId, exportId ) }/enable` );
	return data;
}

/** Disable never re-owns: switching something off widens nothing. */
export async function disableExportDefinition( collectionId, exportId ) {
	const { data } = await api.post( `${ API_EXPORT_DEF_URL( collectionId, exportId ) }/disable` );
	return data;
}

/**
 * Render it NOW, synchronously, as the definition's owner — and deliver it to
 * the recipients exactly as a scheduled run would. Returns the run row.
 */
export async function runExportNow( collectionId, exportId ) {
	const { data } = await api.post( `${ API_EXPORT_DEF_URL( collectionId, exportId ) }/run` );
	return data;
}

/** Newest first; `limit` 1..100 (the API's default is 25). */
export async function getExportRuns( collectionId, exportId, { limit, skip } = {} ) {
	const params = {};
	if ( limit !== undefined ) params.limit = limit;
	if ( skip !== undefined ) params.skip = skip;
	const { data } = await api.get( API_EXPORT_RUNS_URL( collectionId, exportId ), { params } );
	return Array.isArray( data?.runs ) ? data.runs : [];
}

/**
 * A run's file, as a blob through the authenticated client. Only the RUN's
 * owner (or an admin) may fetch it — anyone else is 403 `not_export_owner`,
 * because the file was rendered through that person's access. 404 once
 * retention has deleted it.
 */
export async function fetchExportRunFile( collectionId, exportId, runId ) {
	const { data } = await api.get(
		API_EXPORT_RUN_FILE_URL( collectionId, exportId, runId ),
		{ responseType: 'blob' }
	);
	return data;
}
